import { useEffect, useRef, useState, useCallback } from 'react';
import noop from 'lodash/noop';
import { Row } from 'talent_matching/types';
import { SidePanelHandle } from 'shared/components/side_panel';
import { CellParamsDef } from '@grnhse/seedling/lib/birch/components/table';

const getCandidatePrefetchBatch = (
  rows: Row[],
  selectedRowIndex: number,
  batchRadius: number
) => {
  const startIndex = Math.max(0, selectedRowIndex - batchRadius);
  const endIndex = Math.min(rows.length, selectedRowIndex + batchRadius + 1);

  return [
    ...rows.slice(startIndex, selectedRowIndex),
    ...rows.slice(selectedRowIndex + 1, endIndex),
  ];
};

type UseSidePanelOptions = {
  rows: Row[];
  preSelectedRow?: Row | null;
  setPreSelectedRow?: (row: Row | null) => void;
};

const useSidePanel = ({
  rows,
  preSelectedRow = null,
  setPreSelectedRow = noop,
}: UseSidePanelOptions) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(!!preSelectedRow);
  const [selectedRow, setSelectedRow] = useState<Row | null>(null);

  const selectedRowIndex =
    selectedRow && rows.findIndex((row) => row.id === selectedRow.id);
  const previousRow =
    selectedRowIndex !== null && selectedRowIndex > 0
      ? rows[selectedRowIndex - 1]
      : null;
  const nextRow =
    selectedRowIndex !== null && selectedRowIndex < rows.length - 1
      ? rows[selectedRowIndex + 1]
      : null;
  const candidatePrefetchBatch =
    selectedRowIndex !== null
      ? getCandidatePrefetchBatch(rows, selectedRowIndex, 2)
      : [];

  const popoverRef = useRef<SidePanelHandle | null>(null);

  useEffect(() => {
    if (preSelectedRow && rows.some((row) => row.id === preSelectedRow.id)) {
      setSelectedRow(preSelectedRow);
      setIsPanelOpen(true);
      setPreSelectedRow(null);
    }
  }, [rows, preSelectedRow, setPreSelectedRow]);

  const handlePanelToggle = useCallback(() => {
    if (isPanelOpen) {
      popoverRef.current?.close();
    } else {
      setIsPanelOpen(true);
    }
  }, [isPanelOpen]);

  const handleRowClick = useCallback(
    (row: Row) => {
      if (!row || !row.id) {
        return;
      }

      if (selectedRow?.id === row.id) {
        if (isPanelOpen) {
          popoverRef.current?.close();
        } else {
          setIsPanelOpen(true);
        }
      } else {
        setSelectedRow(row);
        setIsPanelOpen(true);
      }
    },
    [selectedRow?.id, isPanelOpen]
  );

  const navigateAfterRemoval = useCallback(() => {
    if (nextRow || previousRow) {
      setPreSelectedRow(nextRow || previousRow || null);
    } else {
      setIsPanelOpen(false);
      setSelectedRow(null);
    }
  }, [nextRow, previousRow, setPreSelectedRow]);

  const getRowClassName = useCallback(
    (params: CellParamsDef) => {
      return isPanelOpen && selectedRow?.id === params.row.id
        ? 'highlighted-row'
        : '';
    },
    [isPanelOpen, selectedRow?.id]
  );

  return {
    isPanelOpen,
    setIsPanelOpen,
    selectedRow,
    selectedRowIndex,
    popoverRef,
    previousRow,
    nextRow,
    candidatePrefetchBatch,
    handlePanelToggle,
    handleRowClick,
    getRowClassName,
    navigateAfterRemoval,
  };
};

export default useSidePanel;
